/**
 * Custom HTML element that loads and displays remote SVG icons.
 * Implements caching to avoid redundant network requests for the same icon.
 *
 * @class RemoteIcon
 * @extends {HTMLElement}

 * @example
 * // Usage in HTML
 * <remote-icon src="/path/to/icon.svg"></remote-icon>
 */
class RemoteIcon extends HTMLElement {
  /** @type {Map<string, string>} */
  static cache = new Map();

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ["src"];
  }

  /**
   * Handles changes to observed attributes.
   *
   * @param {string} name - The name of the attribute that changed
   * @param {string} oldValue - The previous value of the attribute
   * @param {string} newValue - The new value of the attribute
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "src" && oldValue !== newValue) {
      this.load();
    }
  }

  /**
   * Loads the SVG content from the specified 'src' attribute.
   * Utilizes caching to prevent redundant network requests.
   */
  async load() {
    const src = this.getAttribute("src");
    if (!src) return;

    if (RemoteIcon.cache.has(src)) {
      this.innerHTML = RemoteIcon.cache.get(src);
      return;
    }

    try {
      const response = await fetch(src);
      if (!response.ok)
        throw new Error("Failed to load SVG: " + response.statusText);
      const svgContent = await response.text();

      if (svgContent.includes("<svg")) {
        RemoteIcon.cache.set(src, svgContent);
        this.innerHTML = svgContent;
      }
    } catch (error) {
      console.error("Error loading icon from " + src + ":", error);
    }
  }
}

customElements.define("remote-icon", RemoteIcon);
